import { ScopeEffectEnum } from '@/interfaces';
import { useMemo } from 'react';
import { ENTITY, RoleLevelEnum, UserActions } from '../enums';
import { useGetMe } from './useGetProfileQuery';

export type ScopeCombinationItem = {
  entity: ENTITY;
  action: UserActions;
  // Scope name is at most entity:action:property - free text for now.
  property?: string;
};

// --- Result key derivation, mirrored at the type level (for autocomplete /
// compile-time checking of the returned object's keys) and at runtime. -----

/**
 * Splits `S` on `Sep` and title-cases each segment - e.g. with sep `-`,
 * 'order-status' -> 'OrderStatus'; with sep `_`,
 * 'UPDATE_ORDER_DETAIL_KIT_SERIAL' -> 'UpdateOrderDetailKitSerial'.
 */
type PascalWords<
  S extends string,
  Sep extends string,
> = S extends `${infer Head}${Sep}${infer Tail}`
  ? `${Capitalize<Lowercase<Head>>}${PascalWords<Tail, Sep>}`
  : Capitalize<Lowercase<S>>;

type ScopeKeyOf<Item> = Item extends {
  entity: infer E extends ENTITY;
  action: infer A extends UserActions;
  property: infer P extends string;
}
  ? `can${PascalWords<`${A}`, '_'>}${PascalWords<`${E}`, '-'>}${Capitalize<P>}`
  : Item extends {
        entity: infer E extends ENTITY;
        action: infer A extends UserActions;
      }
    ? `can${PascalWords<`${A}`, '_'>}${PascalWords<`${E}`, '-'>}`
    : never;

// Only the first {entity, action, property} of a combination names the key -
// the rest are extra AND-conditions that only affect the boolean value.
type ComboKey<Combo> = Combo extends readonly [infer First, ...any[]]
  ? ScopeKeyOf<First>
  : never;

type Prettify<T> = { [K in keyof T]: T[K] } & {};

type BuildScopeResult<
  Combos extends readonly (readonly ScopeCombinationItem[])[],
> = Combos extends readonly [
  infer Head extends readonly ScopeCombinationItem[],
  ...infer Rest extends readonly (readonly ScopeCombinationItem[])[],
]
  ? Record<ComboKey<Head>, boolean> & BuildScopeResult<Rest>
  : unknown;

// `combinations` is only known as a literal tuple when it's an inline array
// literal (thanks to the `const` type param below). When it's built
// dynamically (e.g. `.map()`), fall back to a plain string-keyed record.
export type ScopeCombinationsResult<
  Combinations extends readonly (readonly ScopeCombinationItem[])[],
> = Combinations extends readonly [
  readonly ScopeCombinationItem[],
  ...(readonly ScopeCombinationItem[])[],
]
  ? Prettify<BuildScopeResult<Combinations>>
  : Record<string, boolean>;

function pascalWords(value: string, sep: string): string {
  return value
    .split(sep)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');
}

function deriveScopeKey({
  entity,
  action,
  property,
}: ScopeCombinationItem): string {
  const actionPascal = pascalWords(action, '_');
  const entityPascal = pascalWords(entity, '-');
  const propertyPascal = property
    ? property.charAt(0).toUpperCase() + property.slice(1)
    : '';
  return `can${actionPascal}${entityPascal}${propertyPascal}`;
}

export type CheckScopeCombinationsOptions = {
  // While the profile is still loading, a combination made up entirely of
  // GET checks defaults to `true` (avoids a flash of hidden UI). Pass
  // `false` for call sites that gate an actual data fetch (e.g. a query's
  // `enabled`) rather than a UI flag: an optimistic `true` there can let a
  // fetch slip through before scopes are known, and - if that query caches
  // aggressively (long staleTime/gcTime) - the result can stick around even
  // after the real (non-optimistic) value resolves to `false`, since
  // flipping `enabled` back off doesn't clear already-fetched cache data.
  optimistic?: boolean;
};

/**
 * Checks entity/action scope permissions, returning an object keyed by
 * `can<Action><Entity><Property?>` (derived from the first
 * {entity, action, property} of each entry in `combinations`) - e.g.
 * `[{ entity: ENTITY.ORDER, action: UserActions.GET, property: 'displayId' }]`
 * produces the key `canGetOrdersDisplayId`. Passing every permission a
 * component needs as one `combinations` array resolves them all in a single
 * hook call instead of one call per permission.
 *
 * Each entry is itself a list of {entity, action, property?} triples that
 * must ALL be satisfied (AND) for that permission to be allowed - most
 * callers pass a single-item list per permission; a multi-item list is for
 * permissions that only make sense when two or more scopes are held
 * together (only the first item names the resulting key). A triple only
 * counts as satisfied when the matching scope's effect is ALLOW.
 */
function useCheckScopeCombinations<
  const Combinations extends readonly (readonly ScopeCombinationItem[])[],
>(
  combinations: Combinations,
  { optimistic = true }: CheckScopeCombinationsOptions = {},
): ScopeCombinationsResult<Combinations> {
  const { data: profile } = useGetMe();
  const loading = !profile;

  const level = profile?.mainRole?.level;
  const scopes = profile?.scopes;

  return useMemo(() => {
    const result: Record<string, boolean> = {};

    combinations.forEach(combination => {
      const key = deriveScopeKey(combination[0]);

      result[key] =
        level === RoleLevelEnum.SUPER_ADMIN
          ? true
          : loading
            ? optimistic &&
              combination.every(({ action }) => action === UserActions.GET)
            : combination.every(({ entity, action, property }) => {
                const scopeKey = property
                  ? `${entity}:${action.toLowerCase()}:${property}`
                  : `${entity}:${action.toLowerCase()}`;
                return scopes?.[scopeKey]?.effect === ScopeEffectEnum.ALLOW;
              });
    });

    return result;
  }, [
    combinations,
    scopes,
    loading,
    level,
    optimistic,
  ]) as ScopeCombinationsResult<Combinations>;
}

export default useCheckScopeCombinations;
